import toast from "react-hot-toast";
import { useState, useMemo, useEffect } from "react";
import { type LibraryDocType, type DocumentType } from "@/utils/types";
import { SearchInput } from "@/components/ui/design-system/SearchInput";
import Button from "@/components/ui/design-system/Button";
import Checkbox from "@/components/ui/design-system/Checkbox";
import { cloneDocumentTypeFromConfigLibrary } from "@/state/server/DocumentType";
import { LibraryTemplateFile } from "./LibraryTemplateFile";
import { useTemplateLibrary } from "./data";
import { createTemplateFileThumbnail } from "./utils";

type LibraryDocTypeViewProps = {
  libraryPath: string[];
  libraryDocType: LibraryDocType;
  onCloneSuccess: (docType: DocumentType) => unknown;
};

const SEARCH_MIN_TEMPLATES = 6;

export const LibraryDocTypeView = ({
  libraryPath,
  libraryDocType,
  onCloneSuccess,
}: LibraryDocTypeViewProps) => {
  const [cloning, setCloning] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((libraryDocType.templates || []).map((t) => t.name))
  );

  const name = libraryPath.at(-1);
  const templateLibrary = useTemplateLibrary();

  // Reset the selection (all selected) whenever the doc type changes.
  useEffect(() => {
    setSelected(new Set((libraryDocType.templates || []).map((t) => t.name)));
  }, [libraryDocType]);

  const filteredTemplates = useMemo(() => {
    const files = (libraryDocType.templates || []).filter((item) =>
      item.name.includes(filterValue)
    );
    files.sort((a, b) => a.name.localeCompare(b.name));
    return files;
  }, [libraryDocType, filterValue]);

  const totalCount = libraryDocType.templates.length;
  const allSelected = selected.size === totalCount && totalCount > 0;

  const toggleTemplate = (templateName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(templateName)) {
        next.delete(templateName);
      } else {
        next.add(templateName);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(
      allSelected
        ? new Set()
        : new Set(libraryDocType.templates.map((t) => t.name))
    );
  };

  const handleClone = async () => {
    if (!libraryPath)
      throw new Error("There is no selected libraryPath to clone");
    const cloningPath = libraryPath.join("/");
    const configNames = Array.from(selected);

    setCloning(true);
    await toast.promise(
      cloneDocumentTypeFromConfigLibrary(cloningPath, configNames),
      {
        loading: "Cloning...",
        success: (docType: DocumentType) => {
          setCloning(false);
          onCloneSuccess(docType);
          return "Cloned successfully";
        },
        error: (e) => {
          setCloning(false);
          return e.message ?? "Unexpected server error, try again.";
        },
      }
    );
  };

  return (
    <div className="mx-2 space-y-ds-6">
      <div className="flex items-center justify-between w-full">
        <h1 className="text-2xl sm:text-4xl font-semibold">{name}</h1>
        <Button
          loading={cloning}
          disabled={selected.size === 0}
          onClick={handleClone}
        >
          Clone selected
          <span className="ml-1.5">({selected.size})</span>
        </Button>
      </div>
      <div className="flex items-center justify-between gap-4">
        <Checkbox
          checked={allSelected}
          indeterminate={selected.size > 0 && !allSelected ? "true" : "false"}
          onChange={toggleSelectAll}
          label={allSelected ? "Deselect all" : "Select all"}
          showPointer
        />
        {libraryDocType.templates.length >= SEARCH_MIN_TEMPLATES && (
          <SearchInput
            placeholder={"Search"}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            onClickClear={() => setFilterValue("")}
            className="font-normal !max-h-[32px] !min-h-[32px] !rounded"
          />
        )}
      </div>
      {templateLibrary && (
        <div className="grid gap-12 grid-cols-1 md:grid-cols-1 md:place-items-stretch lg:grid-cols-2 xl:grid-cols-3 max-w-screen-xl">
          {filteredTemplates.map((template) => {
            const thumbnail = createTemplateFileThumbnail({
              baseUrl: templateLibrary.baseUrl,
              path: libraryPath,
              template: template,
            });

            return (
              <LibraryTemplateFile
                key={template.name}
                name={template.name}
                thumbnail={thumbnail}
                checked={selected.has(template.name)}
                onToggle={() => toggleTemplate(template.name)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
